/**
 * App Settings
 * Configuration for patient forms and dropdowns
 * Based on appConfig structure from bqContent
 *
 * This module supports both simple and complex treatment type structures
 */

/**
 * Gender options
 */
export const GENDER_OPTIONS = [
  { id: 'MALE', name: 'Male' },
  { id: 'FEMALE', name: 'Female' }
];

/**
 * Caregiver relationship options
 */
export const RELATION_OPTIONS = [
  { id: 'PARTNER', name: 'Partner' },
  { id: 'SON', name: 'Son' },
  { id: 'DAUGHTER', name: 'Daughter' },
  { id: 'SIBLING', name: 'Sibling' },
  { id: 'PROFESSIONAL', name: 'Professional' },
  { id: 'OTHER', name: 'Other' },
  { id: 'NA', name: 'N/A' }
];

/**
 * Unit Systems for height/weight
 */
export const UNIT_SYSTEMS = {
  METRIC: {
    id: 'METRIC',
    name: 'Metric',
    height: { unit: 'cm', label: 'Height (cm)', placeholder: '175' },
    weight: { unit: 'kg', label: 'Weight (kg)', placeholder: '70' }
  },
  IMPERIAL: {
    id: 'IMPERIAL',
    name: 'Imperial',
    height: { unit: 'ft/in', label: "Height (ft'in)", placeholder: "5'9" },
    weight: { unit: 'lbs', label: 'Weight (lbs)', placeholder: '154' }
  }
};

/**
 * Treatment Types (Neurological Injury Types)
 * Based on appConfig structure from bqContent
 */
export const TREATMENT_TYPES = {
  label: 'Type of Injury',
  choices: [
    {
      id: 'STROKE',
      name: 'Stroke',
      setId: 1,
      variantsLabel: 'Affected side of the body',
      variantType: 'singleSelect',
      variants: [
        { id: 'RIGHT', name: 'Right', setId: 0 },
        { id: 'LEFT', name: 'Left', setId: 2 },
        { id: 'BOTH', name: 'Both', setId: 0 }
      ],
      medicalQuestions: [
        {
          id: 'mRS',
          name: 'mRS (modified Rankin Scale)',
          type: 'number',
          min: 0,
          max: 5
        },
        {
          id: 'FAC',
          name: 'Functional Ambulation Category',
          type: 'number',
          min: 0,
          max: 6
        },
        {
          id: 'MMSE',
          name: 'MMSE (Mini-Mental State Examination)',
          type: 'number',
          min: 0,
          max: 30
        }
      ],
      // Additional fields required for MARKET clinic types
      additionalInfo: {
        clinicTypes: ['MARKET', 'MARKET_DEMO'],
        questions: {
          acuteIntervention: {
            label: 'Acute intervention',
            type: 'multiSelect',
            required: true,
            options: [
              { id: 'NO', name: 'No' },
              { id: 'IV_THROMBOLYTICS', name: 'Yes, IV Thrombolytics' },
              { id: 'ENDOVASCULAR_RECANALIZATION', name: 'Yes, Endovascular recanalization (ERT)' },
              { id: 'CAROTID_ANGIOPLASTY', name: 'Yes, Carotid angioplasty' },
              { id: 'CAROTID_ENDARTERECTOMY', name: 'Yes, Carotid endarterectomy' },
              { id: 'NA', name: 'N/A' }
            ]
          },
          affectedHemisphere: {
            label: 'Affected hemisphere',
            type: 'singleSelect',
            required: true,
            options: [
              { id: 'RIGHT', name: 'Right' },
              { id: 'LEFT', name: 'Left' },
              { id: 'BILATERAL', name: 'Bilateral' },
              { id: 'NA', name: 'N/A' }
            ]
          },
          affectedRegion: {
            label: 'Affected region',
            type: 'multiSelect',
            required: true,
            options: [
              { id: 'CORTEX', name: 'Cortex' },
              { id: 'SUB_CORTEX', name: 'Sub-cortex' },
              { id: 'BRAINSTEM', name: 'Brainstem' },
              { id: 'CEREBELLUM', name: 'Cerebellum' },
              { id: 'NA', name: 'N/A' }
            ]
          },
          mechanism: {
            label: 'Mechanism',
            type: 'singleSelect',
            required: true,
            options: [
              { id: 'LARGE_ARTERY_ATHEROSCLEROSIS', name: 'Large-artery atherosclerosis (embolus/thrombosis)' },
              { id: 'SMALL_VESSEL_OCCLUSION', name: 'Small-vessel occlusion (lacune)' },
              { id: 'CARDIOEMBOLISM', name: 'Cardioembolism (high-risk/medium-risk)' },
              { id: 'OTHER_DETERMINED_ETIOLOGY', name: 'Stroke of other determined etiology' },
              { id: 'UNDETERMINED_ETIOLOGY_A', name: 'Stroke of other undetermined etiology - Two or more causes identified' },
              { id: 'UNDETERMINED_ETIOLOGY_B', name: 'Stroke of other undetermined etiology - Negative evaluation' },
              { id: 'UNDETERMINED_ETIOLOGY_C', name: 'Stroke of other undetermined etiology - Incomplete evaluation' },
              { id: 'NA', name: 'N/A' }
            ]
          }
        }
      }
    }
  ]
};

/**
 * Medical Deficits options
 */
export const DEFICITS_OPTIONS = [
  { id: 'reading_writing', name: 'Reading/writing' },
  { id: 'speaking', name: 'Speaking' },
  { id: 'balance_coordination', name: 'Balance/coordination' },
  { id: 'visual_problems', name: 'Visual problems' },
  { id: 'numbness', name: 'Numbness' },
  { id: 'weakness', name: 'Weakness/loss of movement' },
  { id: 'swallowing', name: 'Difficulty swallowing' },
  { id: 'spasticity', name: 'Spasticity/hypertonicity' },
  { id: 'breathing', name: 'Difficulty breathing' },
  { id: 'bowel_bladder', name: 'Bowel/bladder deficits' },
  { id: 'mood_changes', name: 'Mood changes' },
  { id: 'anxiety_depression', name: 'Anxiety/depression' },
  { id: 'other', name: 'Other' }
];

/**
 * Get treatment type by ID
 */
export const getTreatmentTypeById = (id) => {
  return TREATMENT_TYPES.choices.find(t => t.id === id);
};

/**
 * Get variants for a treatment type
 */
export const getVariantsForTreatmentType = (treatmentTypeId) => {
  const treatmentType = getTreatmentTypeById(treatmentTypeId);
  return treatmentType?.variants || [];
};

/**
 * Get medical questions for a treatment type
 */
export const getMedicalQuestionsForTreatmentType = (treatmentTypeId) => {
  const treatmentType = getTreatmentTypeById(treatmentTypeId);
  return treatmentType?.medicalQuestions || [];
};

/**
 * Get additionalInfo questions for a treatment type and clinic type
 * These are additional required fields for specific clinic types (e.g., MARKET)
 */
export const getAdditionalInfoForTreatmentType = (treatmentTypeId, clinicType) => {
  const treatmentType = getTreatmentTypeById(treatmentTypeId);
  const additionalInfo = treatmentType?.additionalInfo;

  // Normalize clinicType for case-insensitive comparison
  const normalizedClinicType = clinicType?.toUpperCase();

  // Check if additionalInfo exists and applies to this clinic type
  if (!additionalInfo || !additionalInfo.clinicTypes?.includes(normalizedClinicType)) {
    return null;
  }

  return additionalInfo.questions || null;
};

/**
 * Check if a clinic type is a MARKET type
 */
export const isMarketClinicType = (clinicType) => {
  return clinicType && clinicType.toUpperCase().includes('MARKET');
};

/**
 * Request Status options
 */
export const REQUEST_STATUS_OPTIONS = [
  { id: 'pending', name: 'Pending', color: 'warning' },
  { id: 'approved', name: 'Approved', color: 'success' },
  { id: 'rejected', name: 'Rejected', color: 'error' }
];

/**
 * Quota Status options
 */
export const QUOTA_STATUS_OPTIONS = [
  { id: 'ok', name: 'Within Quota', color: 'success' },
  { id: 'warning', name: 'Approaching Limit', color: 'warning' },
  { id: 'exceeded', name: 'Exceeded', color: 'error' },
  { id: 'no_quota', name: 'No Quota Set', color: 'default' }
];
